import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import React, { Suspense } from "react";
import * as ReactDOM from "react-dom/client";
import { App } from "./app/App";
import {
  AppThemeProvider,
  ThemeName,
  useAppTheme,
} from "shared-ts-ui-app-theme-provider";
import { WindowSizeProvider } from "shared-ts-ui-responsive";
import { darkTheme } from "./theme/darkTheme";
import { lightTheme } from "./theme/lightTheme";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { appInfo } from "./appInfo";
import {
  StringSetting,
  UserSettingsProvider,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MsalAuthentication } from "shared-ts-utils-msal-authentication";
import { With } from "shared-ts-ui-react";
import {
  I18NextProvider,
  normalizeInitialUserFacingLanguage,
} from "shared-ts-utils-i18n";
import { languages } from "./languages";
import { InteractionType } from "@azure/msal-browser";
import { AppInfoProvider } from "shared-ts-app-info";
import {
  BootingPage,
  BootPage,
  CenteredCircularProgress,
} from "shared-ts-ui-app-boot";
import { NotificationContextProvider } from "shared-ts-ui-notification";
import { AppLayout } from "shared-ts-ui-app-layout";
import { SystemDao } from "firefly-my-chimney-users-ts-system";
import { ServerEnvironmentProvider } from "shared-ts-server-environment";
import { LocaleProvider } from "shared-ts-locale";
import { LanguageUserSettingProvider } from "shared-ts-language";
import { MuiLocalizationProvider } from "shared-ts-mui";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const systemDao = new SystemDao();
root.render(
  <Suspense fallback={<CenteredCircularProgress />}>
    <ServerEnvironmentProvider
      getServerEnvironmentInfo={systemDao.getServerEnvironmentInfo}
    >
      {() => (
        <QueryClientProvider client={queryClient}>
          <WindowSizeProvider>
            <UserSettingsProvider appKey={appInfo.key}>
              {(settings) => {
                const resolvedLanguage = normalizeInitialUserFacingLanguage(
                  localStorage.getItem("ff-mcu::language"),
                  "nb-NO",
                );

                return (
                  <AppThemeProvider
                    initialTheme={settings.readString("theme")}
                    themes={{
                      dark: darkTheme,
                      light: lightTheme,
                    }}
                  >
                    <AppLayout>
                      <I18NextProvider
                        defaultVersion={appInfo.buildVersion ?? null}
                        language={resolvedLanguage}
                        languages={languages}
                        fallbackLng={["en"]}
                        debug={localStorage.getItem("i18next.debug") === "true"}
                      >
                        <AppInfoProvider
                          appInfo={appInfo}
                          getBuildNumber={systemDao.getBuildNumber}
                          loadingComponent={(buildNumber) => (
                            <BootingPage
                              appInfo={appInfo}
                              queries={{ buildNumber }}
                            >
                              {() => null}
                            </BootingPage>
                          )}
                        >
                          {(appInfo) => {
                            return (
                              <MsalAuthentication
                                tenantId={
                                  "491d2fb8-4f98-4fa8-a4f1-667d444bfd37"
                                }
                                clientId={
                                  "0ac23798-366c-44b2-b206-0f28c03aa31d"
                                }
                                scopes={[
                                  "0ac23798-366c-44b2-b206-0f28c03aa31d/.default",
                                ]}
                                method={InteractionType.Redirect}
                                predicate={() => {
                                  return window.location.protocol === "https:";
                                }}
                                authenticated={(user) => {
                                  return (
                                    <LocalizationProvider
                                      dateAdapter={AdapterLuxon}
                                      adapterLocale={"no"}
                                    >
                                      <UserSettingsProvider>
                                        <LanguageUserSettingProvider
                                          defaultLanguage={"nb-NO"}
                                        >
                                          {(language) => (
                                            <LocaleProvider locale={language}>
                                              <MuiLocalizationProvider
                                                language={language}
                                              >
                                                <With>
                                                  {() => {
                                                    const {
                                                      themeName,
                                                      setTheme,
                                                    } = useAppTheme();
                                                    const [theme] =
                                                      useUserSettingWithDefault(
                                                        "theme",
                                                        StringSetting,
                                                        themeName,
                                                      );
                                                    setTheme(
                                                      theme as ThemeName,
                                                    );

                                                    return (
                                                      <NotificationContextProvider>
                                                        <App />
                                                      </NotificationContextProvider>
                                                    );
                                                  }}
                                                </With>
                                              </MuiLocalizationProvider>
                                            </LocaleProvider>
                                          )}
                                        </LanguageUserSettingProvider>
                                      </UserSettingsProvider>
                                    </LocalizationProvider>
                                  );
                                }}
                                unauthenticated={() => {
                                  return (
                                    <BootPage
                                      appName={"MyChimney Users"}
                                      messages={[]}
                                    />
                                  );
                                }}
                              />
                            );
                          }}
                        </AppInfoProvider>
                      </I18NextProvider>
                    </AppLayout>
                  </AppThemeProvider>
                );
              }}
            </UserSettingsProvider>
          </WindowSizeProvider>
        </QueryClientProvider>
      )}
    </ServerEnvironmentProvider>
  </Suspense>,
);
